// ============================================
// FILE: src/components/fashion/WriteReviewModal.jsx
// ============================================
//
// Only reachable for customers the API has already said may review, so this
// form does not re-litigate eligibility — it validates shape and reports
// whatever the server decides.

import React, { useState, useEffect } from 'react';
import { X, ImagePlus, Trash2 } from 'lucide-react';
import { StarPicker } from './StarRating';
import apiService from '../../apiService';
import { getProductImageUrl, handleProductImageError } from '../../utils/imageHelpers';

const MAX_IMAGES = 5;
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_BODY = 4000;

const WriteReviewModal = ({
  isOpen,
  onClose,
  product,
  existingReview = null,
  onSaved = () => {},
  showToast,
  isDesktop = false,
}) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);          // { file, preview }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEdit = Boolean(existingReview);

  useEffect(() => {
    if (!isOpen) return;
    setRating(existingReview?.rating || 0);
    setTitle(existingReview?.title || '');
    setBody(existingReview?.body || '');
    setFiles([]);
    setError('');
  }, [isOpen, existingReview]);

  // Object URLs are leaked if not revoked when the previews go away.
  useEffect(() => () => files.forEach((f) => URL.revokeObjectURL(f.preview)), [files]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  if (!isOpen) return null;

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const room = MAX_IMAGES - files.length;

    if (room <= 0) {
      setError(`You can attach up to ${MAX_IMAGES} photos.`);
      return;
    }

    const tooBig = incoming.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      setError(`"${tooBig.name}" is over 5 MB.`);
      return;
    }

    setError('');
    setFiles((current) => [
      ...current,
      ...incoming.slice(0, room).map((file) => ({ file, preview: URL.createObjectURL(file) })),
    ]);
  };

  const removeFile = (index) => {
    setFiles((current) => {
      URL.revokeObjectURL(current[index]?.preview);
      return current.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async () => {
    if (rating < 1) {
      setError('Please choose a star rating.');
      return;
    }

    setSaving(true);
    setError('');

    const resp = isEdit
      ? await apiService.updateReview(product.id, existingReview.id, { rating, title, body })
      : await apiService.submitReview(product.id, {
          rating, title, body, images: files.map((f) => f.file),
        });

    setSaving(false);

    if (!resp?.success) {
      setError(resp?.data?.message || 'Could not save your review. Please try again.');
      return;
    }

    showToast?.(isEdit ? 'Review updated' : 'Thanks for your review!', 'success');
    onSaved(resp.data?.review || null);
    onClose();
  };

  return (
    <div className="fashion-scope fixed inset-0 z-[330] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />

      <div
        className="relative bg-white w-full overflow-y-auto"
        style={{
          maxWidth: isDesktop ? 560 : '100%',
          maxHeight: '90vh',
          borderRadius: isDesktop ? 4 : '12px 12px 0 0',
        }}
      >
        <div
          className="sticky top-0 bg-white flex items-center justify-between px-5 py-3"
          style={{ borderBottom: '1px solid var(--fx-line)' }}
        >
          <span className="fx-label">{isEdit ? 'Edit your review' : 'Write a review'}</span>
          <button onClick={onClose} className="p-1 -mr-1" aria-label="Close">
            <X size={20} style={{ color: 'var(--fx-ink-2)' }} />
          </button>
        </div>

        <div className="px-5 py-5">
          {/* What is being reviewed */}
          <div className="flex items-center gap-3 pb-4 mb-5" style={{ borderBottom: '1px solid var(--fx-line)' }}>
            <div className="flex-shrink-0 bg-[#f5f5f6] overflow-hidden" style={{ width: 48, height: 62, borderRadius: 2 }}>
              <img src={getProductImageUrl(product)} alt="" className="w-full h-full object-cover" onError={handleProductImageError} />
            </div>
            <div className="min-w-0">
              <p className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{product?.brand || product?.name}</p>
              {product?.brand && (
                <p className="truncate" style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>{product.name}</p>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label className="fx-label block mb-2">Your rating *</label>
            <StarPicker value={rating} onChange={setRating} />
          </div>

          <div className="fx-field">
            <label htmlFor="rev-title">Headline</label>
            <input
              id="rev-title"
              type="text"
              className="fx-input"
              maxLength={150}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Sum it up in a few words"
            />
          </div>

          <div className="fx-field">
            <label htmlFor="rev-body">Your review</label>
            <textarea
              id="rev-body"
              rows={5}
              className="fx-input"
              maxLength={MAX_BODY}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="How was the fit, fabric and quality?"
            />
            <p className="mt-1 text-right" style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>
              {body.length}/{MAX_BODY}
            </p>
          </div>

          {/* Photos are only offered on create — editing them would need
              add/remove endpoints this feature does not have yet. */}
          {!isEdit && (
            <div className="fx-field">
              <label>Add photos ({files.length}/{MAX_IMAGES})</label>

              <div className="flex gap-2 flex-wrap">
                {files.map((f, i) => (
                  <div
                    key={f.preview}
                    className="relative overflow-hidden"
                    style={{ width: 72, height: 72, borderRadius: 4, border: '1px solid var(--fx-line)' }}
                  >
                    <img src={f.preview} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="absolute top-0.5 right-0.5 p-1 rounded-full bg-black/60"
                      aria-label="Remove photo"
                    >
                      <Trash2 size={11} className="text-white" />
                    </button>
                  </div>
                ))}

                {files.length < MAX_IMAGES && (
                  <label
                    className="flex flex-col items-center justify-center cursor-pointer"
                    style={{ width: 72, height: 72, borderRadius: 4, border: '1px dashed var(--fx-line)' }}
                  >
                    <ImagePlus size={18} style={{ color: 'var(--fx-ink-3)' }} />
                    <span style={{ fontSize: 10, color: 'var(--fx-ink-3)', marginTop: 2 }}>Add</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      className="hidden"
                      onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />
                  </label>
                )}
              </div>
              <small className="text-muted" style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>
                JPG, PNG or WebP, up to 5 MB each.
              </small>
            </div>
          )}

          {error && (
            <p className="mb-3" style={{ fontSize: 13, color: 'var(--fx-pink)' }}>{error}</p>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="fx-btn fx-btn-primary w-full"
          >
            {saving ? 'Submitting…' : isEdit ? 'Update Review' : 'Submit Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WriteReviewModal;
